/**
 * Extracts `CopilotChartData` from Flow/AG-UI tool calls and results.
 *
 * - `create_chart` and causal-impact tools: chart is in the call arguments
 * - forecast tools: chart is in the tool result (columnar history/forecast blocks)
 */

import { type ToolCall as AgUiToolCall } from '@ag-ui/client'
import { isRecord, parseJsonContent, parseToolCallArgumentsForDisplay } from 'shared/copilot/toolRegistry/parsing'
import { type CopilotChartData, type CopilotChartType } from './types'

const VALID_CHART_TYPES = new Set<CopilotChartType>(['line', 'bar', 'area', 'scatter'])

const CAUSAL_IMPACT_FUNCTIONS = new Set(['analyze_single_series_causal_impact', 'analyze_diff_in_diff_causal_impact'])

const FORECAST_FUNCTIONS = new Set(['forecast_time_series', 'forecast_ratio_metric'])

/** Reads a tool arg by snake_case name, falling back to camelCase. */
const getArg = (args: Record<string, unknown>, snake: string): unknown => {
  if (snake in args) return args[snake]
  const camel = snake.replaceAll(/_([a-z])/g, (_, char: string) => char.toUpperCase())
  if (camel in args) return args[camel]
  return undefined
}

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined

const stampChart = (chart: Omit<CopilotChartData, 'sourceCallId'>, sourceCallId: string): CopilotChartData => ({
  ...chart,
  sourceCallId
})

/**
 * Maps Flow `create_chart` arguments onto `CopilotChartData`.
 * Returns null when required fields (`data`, valid `chart_type`) are missing.
 */
const tryExtractCreateChart = (args: Record<string, unknown>): Omit<CopilotChartData, 'sourceCallId'> | null => {
  const data = getArg(args, 'data')
  const chartType = getArg(args, 'chart_type')
  const xField = getArg(args, 'x_field')
  const yField = getArg(args, 'y_field')
  const title = getArg(args, 'title')
  const colorField = getArg(args, 'color_field')
  const sortBy = getArg(args, 'sort_by')

  if (!Array.isArray(data) || typeof chartType !== 'string' || !VALID_CHART_TYPES.has(chartType as CopilotChartType)) {
    return null
  }

  return {
    chartType: chartType as CopilotChartType,
    data: data as Array<Record<string, unknown>>,
    xField: typeof xField === 'string' ? xField : '',
    yField: typeof yField === 'string' ? yField : '',
    title: typeof title === 'string' ? title : '',
    colorField: typeof colorField === 'string' ? colorField : '',
    sortBy: typeof sortBy === 'string' ? sortBy : ''
  }
}

/**
 * Builds a line chart from causal-impact tool args.
 * Diff-in-diff merges test + control series under `_group`; single-series omits color.
 */
const tryExtractCausalImpactChart = (args: Record<string, unknown>): Omit<CopilotChartData, 'sourceCallId'> | null => {
  const timeSeries = getArg(args, 'time_series')
  const controlSeries = getArg(args, 'control_series')
  const dateField = getArg(args, 'date_field')
  const valueField = getArg(args, 'value_field')
  const metricName = getArg(args, 'metric_name')
  const interventionDateArg = getArg(args, 'intervention_date')

  if (!Array.isArray(timeSeries) || typeof dateField !== 'string' || typeof valueField !== 'string') {
    return null
  }

  const rawDate = String(interventionDateArg ?? '')
  const normalizedDate = rawDate.includes('-') ? rawDate.replaceAll('-', '') : rawDate
  const interventionDate = normalizedDate.length > 0 ? normalizedDate : undefined
  const metricLabel = typeof metricName === 'string' ? metricName : valueField

  if (Array.isArray(controlSeries) && controlSeries.length > 0) {
    const combined = [
      ...(timeSeries as Array<Record<string, unknown>>).map((row) => ({ ...row, _group: 'Test' })),
      ...(controlSeries as Array<Record<string, unknown>>).map((row) => ({ ...row, _group: 'Control' }))
    ]

    return {
      chartType: 'line',
      data: combined,
      xField: dateField,
      yField: valueField,
      title: `Diff-in-Diff: ${metricLabel}`,
      colorField: '_group',
      sortBy: '',
      interventionDate
    }
  }

  return {
    chartType: 'line',
    data: timeSeries as Array<Record<string, unknown>>,
    xField: dateField,
    yField: valueField,
    title: `Causal Impact: ${metricLabel}`,
    colorField: '',
    sortBy: '',
    interventionDate
  }
}

/**
 * Converts a columnar block (`{ date: [...], value: [...] }`) into row objects.
 * Used for forecast history/forecast payloads from the Flow backend.
 */
const expandColumns = (block: unknown): Array<Record<string, unknown>> => {
  if (!isRecord(block)) return []
  const keys = Object.keys(block)
  if (keys.length === 0) return []
  const firstCol = block[keys[0]]
  if (!Array.isArray(firstCol)) return []
  const len = firstCol.length
  const rows: Array<Record<string, unknown>> = []

  for (let index = 0; index < len; index++) {
    const row: Record<string, unknown> = {}
    for (const key of keys) {
      const column = block[key]
      row[key] = Array.isArray(column) ? column[index] : null
    }
    rows.push(row)
  }

  return rows
}

/**
 * Builds a forecast chart from a successful forecast tool result.
 *
 * Backend history/forecast rows are always `{ date, value, ... }`. Metadata
 * `value_column` is the display metric name (e.g. `sessions`); we copy `value`
 * onto that field so ChartCard adapters read the intended metric.
 */
const tryExtractForecastChart = (
  toolName: string,
  rawResponse: unknown
): Omit<CopilotChartData, 'sourceCallId'> | null => {
  if (!FORECAST_FUNCTIONS.has(toolName) || !isRecord(rawResponse)) {
    return null
  }

  const payloadCandidate = isRecord(rawResponse.result) ? rawResponse.result : rawResponse
  if (payloadCandidate.status !== 'success') return null

  const historyRows = expandColumns(payloadCandidate.history)
  const forecastRows = expandColumns(payloadCandidate.forecast)
  if (historyRows.length === 0 && forecastRows.length === 0) return null

  const metadata = isRecord(payloadCandidate.metadata) ? payloadCandidate.metadata : {}
  const yField = typeof metadata.value_column === 'string' ? metadata.value_column : 'value'

  const mapForecastRow = (row: Record<string, unknown>, series: 'history' | 'forecast'): Record<string, unknown> => {
    const mapped: Record<string, unknown> = { ...row, _series: series }
    if (yField !== 'value' && row.value != null) {
      mapped[yField] = row.value
    }
    return mapped
  }

  return {
    chartType: 'forecast',
    data: [
      ...historyRows.map((row) => mapForecastRow(row, 'history')),
      ...forecastRows.map((row) => mapForecastRow(row, 'forecast'))
    ],
    xField: 'date',
    yField,
    title: typeof metadata.title === 'string' ? metadata.title : 'Forecast',
    colorField: '_series',
    sortBy: '',
    forecastCutoffDate: asNonEmptyString(metadata.forecast_cutoff_date)
  }
}

const tryExtractChartFromCallArgs = (
  toolName: string,
  args: Record<string, unknown>
): Omit<CopilotChartData, 'sourceCallId'> | null => {
  if (toolName === 'create_chart') return tryExtractCreateChart(args)
  if (CAUSAL_IMPACT_FUNCTIONS.has(toolName)) return tryExtractCausalImpactChart(args)
  return null
}

/** Extracts a chart from tool-call arguments (`create_chart`, causal impact). */
export const extractChartFromToolCall = (toolCall: AgUiToolCall): CopilotChartData | null => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) return null

  const chart = tryExtractChartFromCallArgs(toolCall.function.name, parsedArgs)
  return chart == null ? null : stampChart(chart, toolCall.id)
}

/** Extracts a forecast chart from a tool result payload. */
export const extractChartFromToolResult = (toolCall: AgUiToolCall, resultContent: unknown): CopilotChartData | null => {
  const parsedContent = parseJsonContent(resultContent)
  const chart = tryExtractForecastChart(toolCall.function.name, parsedContent)
  return chart == null ? null : stampChart(chart, toolCall.id)
}

/** Stores a chart in the call-id → chart map when non-null (used while reducing AG-UI messages). */
export const addChartToMap = <Chart extends { sourceCallId: string }>(
  map: Map<string, Chart>,
  chart: Chart | null
): void => {
  if (chart != null) {
    map.set(chart.sourceCallId, chart)
  }
}
